/**
 * Evaluates a single condition based on element type and operation
 */
const isEmptyValue = (value) => {
  return (
    value === undefined ||
    value === null ||
    (typeof value === 'string' && value.trim() === '') ||
    (Array.isArray(value) && value.length === 0)
  );
};

const evaluateCondition = (condition, formData, element) => {
  const elementValue = formData[condition.elementId];
  const compareValue = condition.value;

  if (condition.operation === 'is_empty') {
    return isEmptyValue(elementValue);
  }

  if (condition.operation === 'is_not_empty') {
    return !isEmptyValue(elementValue);
  }

  // Handle undefined or null values
  if (elementValue === undefined || elementValue === null) {
    return false;
  }

  switch (element?.type) {
    case 'switch':
      const boolValue = String(compareValue).toLowerCase() === 'true';
      return condition.operation === 'equals'
        ? elementValue === boolValue
        : elementValue !== boolValue;

    case 'multiple-choice':
      // Convert the selected IDs to their content values if needed
      const selectedValues = Array.isArray(elementValue) ? elementValue : [elementValue];

      switch (condition.operation) {
        case 'contains':
          return selectedValues.includes(compareValue);
        case 'not_contains':
          return !selectedValues.includes(compareValue);
        case 'equals':
          return selectedValues.length === 1 && selectedValues[0] === compareValue;
        case 'not_equals':
          return selectedValues.length !== 1 || selectedValues[0] !== compareValue;
        default:
          return false;
      }

    // CPQ number options are mapped to element type `slider` (input or slider UI)
    case 'number':
    case 'slider': {
      const numValue = Number(elementValue);
      const numCompare = Number(compareValue);

      switch (condition.operation) {
        case 'equals':
          return numValue === numCompare;
        case 'not_equals':
          return numValue !== numCompare;
        case 'greater_than':
          return numValue > numCompare;
        case 'greater_than_or_equal':
          return numValue >= numCompare;
        case 'less_than':
          return numValue < numCompare;
        case 'less_than_or_equal':
          return numValue <= numCompare;
        default:
          return false;
      }
    }

    case 'text':
      const strValue = String(elementValue).toLowerCase();
      const strCompare = String(compareValue).toLowerCase();

      switch (condition.operation) {
        case 'equals':
          return strValue === strCompare;
        case 'not_equals':
          return strValue !== strCompare;
        case 'contains':
          return strValue.includes(strCompare);
        default:
          return false;
      }

    case 'kvk':
      // KvK elements store objects with company data
      if (typeof elementValue === 'object' && elementValue !== null) {
        const kvkName = elementValue.name?.toLowerCase() || '';
        const kvkNumber = elementValue.kvkNumber?.toLowerCase() || '';
        const searchValue = String(compareValue).toLowerCase();

        switch (condition.operation) {
          case 'equals':
            return kvkNumber === searchValue || kvkName === searchValue;
          case 'not_equals':
            return kvkNumber !== searchValue && kvkName !== searchValue;
          case 'contains':
            return kvkName.includes(searchValue) || kvkNumber.includes(searchValue);
          default:
            return false;
        }
      } else if (typeof elementValue === 'string') {
        // Fallback for string values
        const strValue = elementValue.toLowerCase();
        const strCompare = String(compareValue).toLowerCase();

        switch (condition.operation) {
          case 'equals':
            return strValue === strCompare;
          case 'not_equals':
            return strValue !== strCompare;
          case 'contains':
            return strValue.includes(strCompare);
          default:
            return false;
        }
      }
      return false;

    default:
      // Default comparison for other types
      switch (condition.operation) {
        case 'equals':
          return Array.isArray(elementValue)
            ? elementValue.includes(compareValue)
            : elementValue === compareValue;
        case 'not_equals':
          return Array.isArray(elementValue)
            ? !elementValue.includes(compareValue)
            : elementValue !== compareValue;
        case 'contains':
          return Array.isArray(elementValue)
            ? elementValue.some((val) => String(val).includes(String(compareValue)))
            : String(elementValue).includes(String(compareValue));
        default:
          return false;
      }
  }
};

/**
 * Evaluates a single rule
 */
const evaluateRule = (rule, formData, form) => {
  const element = form.pages
    .flatMap((page) => page.elements)
    .find((element) => element.id === rule.elementId);

  return evaluateCondition(rule, formData, element);
};

/**
 * Gedeelde kern: evalueert een conditie-envelope
 * (`{ type, betweenGroupLogic, groups: [{ logic, rules, subGroups }] }`).
 *
 * Hoe één regel wordt geëvalueerd is geïnjecteerd via `evalRule`, zodat zowel
 * het element-formaat (`evaluateElementConditions`) als het ruwe CPQ-formaat
 * (`evaluateCPQOptionConditions`) exact dezelfde groep-/logica-recursie delen.
 *
 * Semantiek:
 * - Geen `groups` (leeg/afwezig) → altijd `true` (geen condities).
 * - `betweenGroupLogic`: tussen groepen (default via `defaultBetweenGroupLogic`).
 * - `group.logic`: binnen een groep (default `AND`).
 * - `group.subGroups`: recursief meegenomen.
 * - `type === 'hide'`: resultaat geïnverteerd.
 * - Lege groep (geen rules/subGroups): natuurlijke `.every()`/`.some()`-uitkomst
 *   (`AND` → true, `OR` → false).
 *
 * @param {Object} conditions - De conditie-envelope
 * @param {Object} cfg
 * @param {Function} cfg.evalRule - (rule) => boolean
 * @param {string} [cfg.defaultBetweenGroupLogic='AND']
 * @returns {boolean}
 */
const evaluateConditionEnvelope = (conditions, { evalRule, defaultBetweenGroupLogic = 'AND' }) => {
  if (!conditions || !Array.isArray(conditions.groups) || conditions.groups.length === 0) {
    return true;
  }

  const betweenGroupLogic = conditions.betweenGroupLogic || defaultBetweenGroupLogic;
  const type = conditions.type || 'show';

  const evalGroup = (group) => {
    if (!group) return true;
    const logic = group.logic || 'AND';
    const ruleResults = (group.rules || []).map((rule) => evalRule(rule));
    const subResults = (group.subGroups || []).map((sub) => evalGroup(sub));
    const all = [...ruleResults, ...subResults];
    return logic === 'OR' ? all.some(Boolean) : all.every(Boolean);
  };

  const result =
    betweenGroupLogic === 'OR'
      ? conditions.groups.some(evalGroup)
      : conditions.groups.every(evalGroup);

  // type "hide": condities voldaan → element verborgen, dus inverteren.
  return type === 'hide' ? !result : result;
};

/**
 * Evaluates if an element should be shown based on its conditions.
 * Werkt op het gemapte element-formaat (`element.props.conditions` met
 * `elementId`/`operation`) en gebruikt per element-type de juiste vergelijking
 * via `evaluateCondition`. Default `betweenGroupLogic` = `OR` (legacy gedrag).
 */
export const evaluateElementConditions = (element, formData, form) => {
  return evaluateConditionEnvelope(element?.props?.conditions, {
    evalRule: (rule) => evaluateRule(rule, formData, form),
    defaultBetweenGroupLogic: 'OR',
  });
};

/**
 * Operator-normalisatie voor het RUWE CPQ optie-conditieformaat.
 * Houd dit in sync met CONDITION_OPERATOR_MAP in useRuntimeConfiguratorGeneration.js.
 */
const CPQ_OPERATOR_ALIASES = {
  '==': 'equals',
  '!=': 'not_equals',
  '>': 'greater_than',
  '>=': 'greater_than_or_equal',
  '<': 'less_than',
  '<=': 'less_than_or_equal',
};

/**
 * Evalueert één ruwe CPQ-conditieregel ({ optionId|attribute, operator, value })
 * tegen een waarden-map. Voor keuzevelden met een array-waarde betekent `equals`
 * "is geselecteerd" (includes) — gelijk aan het render-gedrag.
 *
 * @param {Object} rule - Conditie-regel
 * @param {Object} values - formData/attributeValues
 * @param {Function} resolveFieldId - (refId) => key in `values`
 * @returns {boolean}
 */
export const evaluateCPQOptionRule = (rule, values, resolveFieldId) => {
  const refId = rule.optionId || rule.attribute;
  if (!refId) return true;

  const rawValue = values[resolveFieldId(refId)];
  const compare = rule.value;
  const operator = CPQ_OPERATOR_ALIASES[rule.operator] || rule.operator || 'equals';

  const isArray = Array.isArray(rawValue);
  const selected = isArray
    ? rawValue
    : rawValue === undefined || rawValue === null || rawValue === ''
      ? []
      : [rawValue];

  switch (operator) {
    case 'equals':
      return isArray ? selected.includes(compare) : String(rawValue) === String(compare);
    case 'not_equals':
      return isArray ? !selected.includes(compare) : String(rawValue) !== String(compare);
    case 'contains':
      return selected.map(String).includes(String(compare));
    case 'not_contains':
      return !selected.map(String).includes(String(compare));
    case 'greater_than':
      return parseFloat(rawValue) > parseFloat(compare);
    case 'greater_than_or_equal':
      return parseFloat(rawValue) >= parseFloat(compare);
    case 'less_than':
      return parseFloat(rawValue) < parseFloat(compare);
    case 'less_than_or_equal':
      return parseFloat(rawValue) <= parseFloat(compare);
    default:
      return String(rawValue) === String(compare);
  }
};

/**
 * Evalueert de RUWE CPQ optie-conditie-envelope
 * (`{ type, betweenGroupLogic, groups: [{ logic, rules, subGroups }] }`) direct
 * tegen een waarden-map, zonder eerst naar het element-formaat te mappen.
 *
 * Single source of truth voor "is deze optie zichtbaar / moet 'm meegerekend
 * worden". Gebruikt door de prijscalculator; backend moet dezelfde semantiek
 * volgen (zie BACKEND_PRICING_CALCULATION_SPEC.md). Deelt de groep-/logica-
 * recursie met `evaluateElementConditions` via `evaluateConditionEnvelope`.
 *
 * KEEP IN SYNC: gekopieerd als smartflow-api/utils/optionConditionsEvaluator.js.
 * Een nieuwe operator of envelope-regel moet in beide repos, anders wordt een
 * optie in de ene wel en in de andere niet getoond en meegerekend.
 *
 * @param {Object} conditions - option.conditions (ruwe CPQ-envelope)
 * @param {Object} values - attributeValues/formData
 * @param {Object} [opts]
 * @param {Function} [opts.resolveFieldId] - map refId → key in `values`.
 *        Default: identity. Configurator: `(id) => `${productId}-${id}-input``
 * @returns {boolean} true als de optie zichtbaar/meegerekend moet worden
 */
export const evaluateCPQOptionConditions = (conditions, values, opts = {}) => {
  const resolveFieldId = opts.resolveFieldId || ((id) => id);
  return evaluateConditionEnvelope(conditions, {
    evalRule: (rule) => evaluateCPQOptionRule(rule, values, resolveFieldId),
    defaultBetweenGroupLogic: 'AND',
  });
};

/**
 * Evaluates all conditions for a block based on operator type (any/all)
 */
export const evaluateBlockConditions = (block, formData) => {
  // Add safety check for undefined block
  if (!block) {
    return true;
  }

  // If no conditions exist, the block is always visible
  if (!block.conditions?.rules?.length) {
    return true;
  }

  const { operator, rules } = block.conditions;

  // Evaluate based on operator type
  return operator === 'any'
    ? rules.some((rule) => evaluateCondition(rule, formData))
    : rules.every((rule) => evaluateCondition(rule, formData));
};

/**
 * Finds the next valid block index based on conditions
 */
export const findNextValidBlockIndex = (currentIndex, blocks, formData) => {
  let nextIndex = currentIndex + 1;

  // Check remaining blocks in the current page
  while (nextIndex < blocks.length) {
    // A block is valid if it has no conditions or its conditions are met
    if (!blocks[nextIndex].conditions || evaluateBlockConditions(blocks[nextIndex], formData)) {
      return nextIndex;
    }
    nextIndex++;
  }

  // Return null if no valid blocks are found
  return null;
};

/**
 * Finds the previous valid block index based on conditions
 */
export const findPreviousValidBlockIndex = (currentIndex, blocks, formData) => {
  let prevIndex = currentIndex - 1;

  while (prevIndex >= 0) {
    if (evaluateBlockConditions(blocks[prevIndex], formData)) {
      return prevIndex;
    }
    prevIndex--;
  }

  return prevIndex;
};

/**
 * Gets all visible elements for a page based on their conditions
 */
export const getVisibleElements = (page, formData) => {
  return page.elements.filter((element) => evaluateElementConditions(element, formData));
};

/**
 * Evaluates page navigation rules to determine the next page
 */
export const evaluatePageNavigationRules = (page, formData, form) => {
  // If no navigation rules exist, return 'next'
  if (!page?.logic?.rules || page.logic.rules.length === 0) {
    return page.logic?.defaultDestination || 'next';
  }

  // Check each rule in order
  for (const rule of page.logic.rules) {
    const { conditions, destination } = rule;
    // Evaluate groups based on betweenGroupLogic
    const result =
      conditions.betweenGroupLogic.binding === 'OR'
        ? conditions.groups.binding.some((group) => {
            // Default to AND logic within groups if not specified
            const logic = group.logic.binding || 'AND';
            return logic === 'OR'
              ? group.rules.some((rule) =>
                  evaluateRule(
                    {
                      elementId: rule.elementId.binding,
                      operation: rule.operation.binding,
                      value: rule.value.binding,
                    },
                    formData,
                    form
                  )
                )
              : group.rules.every((rule) =>
                  evaluateRule(
                    {
                      elementId: rule.elementId.binding,
                      operation: rule.operation.binding,
                      value: rule.value.binding,
                    },
                    formData,
                    form
                  )
                );
          })
        : conditions.groups.binding.every((group) => {
            const logic = group.logic.binding || 'AND';
            return logic === 'OR'
              ? group.rules.some((rule) =>
                  evaluateRule(
                    {
                      elementId: rule.elementId.binding,
                      operation: rule.operation.binding,
                      value: rule.value.binding,
                    },
                    formData,
                    form
                  )
                )
              : group.rules.every((rule) =>
                  evaluateRule(
                    {
                      elementId: rule.elementId.binding,
                      operation: rule.operation.binding,
                      value: rule.value.binding,
                    },
                    formData,
                    form
                  )
                );
          });
    // If conditions are met, return this destination
    if (result) {
      return destination;
    }
  }

  // If no rules match, return the default destination
  return page.logic?.defaultDestination || 'next';
};

export { evaluateCondition, evaluateRule };
